import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import AdminUiLabelsPanel from '../../components/BusinessSettings/AdminUiLabelsPanel';

/**
 * Shown from the admin sidebar while a Super Admin is impersonating a business,
 * so platform tools stay under Owner routes but labels are editable in context.
 */
const BusinessUiLabels: React.FC = () => {
  const businessId = localStorage.getItem('businessId');
  const impersonating = localStorage.getItem('impersonatingBusiness') === 'true';
  const [adminRaw, setAdminRaw] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchLabels = async () => {
    if (!businessId || !impersonating) {
      setLoading(false);
      return;
    }
    setLoadError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_BASE_URL}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdminRaw(res.data?.admin_ui_labels);
    } catch (e: unknown) {
      const msg =
        axios.isAxiosError(e) && e.response?.data?.message
          ? String(e.response.data.message)
          : 'Could not load business.';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per mount / context
  }, [businessId, impersonating]);

  if (!impersonating) {
    return (
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Alert severity="info">
          This page is only available while you are <strong>impersonating a business</strong> (Owner
          dashboard → choose a business → log into business). Super Admins can also edit labels under{' '}
          <strong>Owner → Business Management → Business settings → Admin UI labels</strong> without
          impersonating.
        </Alert>
      </Container>
    );
  }

  if (!businessId) {
    return (
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Alert severity="warning">No business is selected in this session.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
        Business UI labels
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customise sidebar and header wording for <strong>this business&apos;s</strong> admin portal.
        Changes apply after admins refresh the page.
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      {!loadError && (
        <AdminUiLabelsPanel
          businessId={businessId}
          adminUiLabelsRaw={adminRaw}
          onSaved={fetchLabels}
        />
      )}
    </Container>
  );
};

export default BusinessUiLabels;
